import { AlertTriangle, PhoneCall, Hospital, Stethoscope, Globe } from "lucide-react";
import { useReveal } from "../../hooks/useReveal";

export const Crisis = () => {
  const ref = useReveal();
  return (
    <section
      id="crisis"
      data-testid="crisis-info-section"
      className="bg-[#FDFBF7]"
    >
      <div className="mx-auto max-w-[1320px] px-6 md:px-10 lg:px-16 py-20 md:py-20 lg:py-24">
        <div
          ref={ref}
          className="reveal bg-[#F6F1EC] border border-[#E5B9A8] p-8 md:p-14 lg:p-20"
        >
          <div className="flex items-center gap-3 text-[#7A3E2A]">
            <AlertTriangle size={20} strokeWidth={1.6} />
            <p className="text-[10px] uppercase tracking-[0.28em]">
              Important Notice
            </p>
          </div>

          <h2 className="mt-6 font-serif-display text-3xl md:text-4xl lg:text-[3rem] leading-[1.1] tracking-[-0.02em] text-[#1A1C1A] font-medium max-w-3xl">
            Are you in a crisis?
          </h2>

          <p className="mt-6 max-w-3xl text-[#4A4D4A] text-base md:text-lg leading-relaxed">
            A crisis includes feeling like you want to hurt yourself or
            someone else, hearing voices, experiencing extreme overwhelming
            distress, or feeling you can no longer cope. This service is not
            for crisis or emergency support. If you are in immediate danger
            or at risk of harm, please use one of the routes below.
          </p>

          {/* Immediate emergency */}
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#7A3E2A]">
                Immediate Emergency
              </p>
              <h3 className="mt-3 font-serif-display text-2xl md:text-3xl text-[#1A1C1A] leading-snug">
                Life-threatening situations
              </h3>
              <p className="mt-4 text-[#4A4D4A] text-base leading-relaxed">
                If you have injured yourself or feel your life is at risk,
                A&amp;E is the right place to go.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
              <a
                href="tel:999"
                data-testid="crisis-999-button"
                className="group flex items-center gap-4 bg-[#1A1C1A] text-[#FDFBF7] hover:bg-[#7A3E2A] px-6 py-6 transition-colors"
              >
                <PhoneCall size={20} strokeWidth={1.6} />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em] opacity-80">
                    Call
                  </p>
                  <p className="font-serif-display text-3xl leading-none mt-1">
                    999
                  </p>
                  <p className="text-xs mt-2 opacity-80">
                    Ambulance or police
                  </p>
                </div>
              </a>
              <div
                data-testid="crisis-ae-card"
                className="flex items-center gap-4 border border-[#1A1C1A]/40 text-[#1A1C1A] px-6 py-6"
              >
                <Hospital size={20} strokeWidth={1.6} />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em] text-[#7A3E2A]">
                    Go to
                  </p>
                  <p className="font-serif-display text-3xl leading-none mt-1">
                    A&amp;E
                  </p>
                  <p className="text-xs mt-2 text-[#4A4D4A]">
                    Your nearest hospital
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="my-12 h-px bg-[#E5B9A8]/70" />

          {/* Urgent, non-life-threatening */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#7A3E2A]">
                Urgent Help
              </p>
              <h3 className="mt-3 font-serif-display text-2xl md:text-3xl text-[#1A1C1A] leading-snug">
                Not immediately life-threatening
              </h3>
              <p className="mt-4 text-[#4A4D4A] text-base leading-relaxed">
                For urgent mental-health support that doesn&rsquo;t require
                emergency services, the following routes are available 24/7.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
              <a
                href="tel:111"
                data-testid="crisis-111-button"
                className="flex items-center gap-4 border border-[#1B2E26] text-[#1B2E26] hover:bg-[#1B2E26] hover:text-[#FDFBF7] px-6 py-6 transition-colors"
              >
                <PhoneCall size={20} strokeWidth={1.6} />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em] opacity-80">
                    Call &middot; Option 2
                  </p>
                  <p className="font-serif-display text-3xl leading-none mt-1">
                    111
                  </p>
                  <p className="text-xs mt-2 opacity-80">
                    NHS mental-health crisis team
                  </p>
                </div>
              </a>
              <a
                href="https://111.nhs.uk/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="crisis-111-online-button"
                className="flex items-center gap-4 border border-[#1B2E26] text-[#1B2E26] hover:bg-[#1B2E26] hover:text-[#FDFBF7] px-6 py-6 transition-colors"
              >
                <Globe size={20} strokeWidth={1.6} />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em] opacity-80">
                    Online
                  </p>
                  <p className="font-serif-display text-2xl leading-none mt-1">
                    111.nhs.uk
                  </p>
                  <p className="text-xs mt-2 opacity-80">
                    24/7 access service
                  </p>
                </div>
              </a>
              <a
                href="tel:116123"
                data-testid="crisis-samaritans-button"
                className="flex items-center gap-4 border border-[#1B2E26] text-[#1B2E26] hover:bg-[#1B2E26] hover:text-[#FDFBF7] px-6 py-6 transition-colors"
              >
                <PhoneCall size={20} strokeWidth={1.6} />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em] opacity-80">
                    Samaritans
                  </p>
                  <p className="font-serif-display text-3xl leading-none mt-1">
                    116 123
                  </p>
                  <p className="text-xs mt-2 opacity-80">
                    Free, 24 hours a day
                  </p>
                </div>
              </a>
              <div
                data-testid="crisis-gp-card"
                className="flex items-center gap-4 border border-[#1B2E26]/30 text-[#1A1C1A] px-6 py-6"
              >
                <Stethoscope size={20} strokeWidth={1.6} className="text-[#1B2E26]" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em] text-[#7A3E2A]">
                    Contact your GP
                  </p>
                  <p className="font-serif-display text-2xl leading-none mt-1">
                    Urgent appointment
                  </p>
                  <p className="text-xs mt-2 text-[#4A4D4A]">
                    Ask the practice for same-day support
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
